/**
 * Recurring-document schedule math — the TypeScript TWIN of the SQL functions
 * `recurring_next_occurrence_date` and `recurring_first_occurrence_on_or_after`
 * (migration `*_recurring_documents.sql`).
 *
 * THE DATABASE IS THE AUTHORITY. The generator (pg_cron sweep + "Run now")
 * advances `next_occurrence_date` with the SQL rule; this module exists so the
 * web can PREVIEW the same dates synchronously (the "Next dates" list under the
 * "Make this recurring" section) without an endpoint. Both implementations are
 * asserted against ONE vector file — `./schedule.vectors.json` — by Vitest
 * here and by the manual SQL suite (`supabase/tests/manual/recurring_documents.sql`,
 * which embeds the same rows as `VALUES`). Change the rule in one place and the
 * other side's test goes red; that is the whole contract.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 *   weekly                    → from + 7 · interval_count days
 *                               (the weekday is start_date's, forever)
 *   monthly/quarterly/yearly  → months = interval_count · {1, 3, 12}
 *                               m1     = date_trunc('month', from) + months
 *                               result = m1 + (LEAST(anchor_day, days_in_month(m1)) − 1)
 *
 * `anchor_day` is the day-of-month of `start_date` (a GENERATED column on the
 * template), and the successor is computed from the ANCHOR, never from the
 * previous occurrence's day. That is what stops the classic drift: Jan 31 →
 * Feb 28 → **Mar 31** (not Mar 28), every-2-months from Jan 31 → Mar 31,
 * May 31, Jul 31, Sep 30, Nov 30, Jan 31; Feb 29 yearly → Feb 28 in non-leap
 * years and back to Feb 29 in the next leap year.
 *
 * ── DATES ARE CALENDAR DAYS ─────────────────────────────────────────────────
 * Every input and output is a plain `YYYY-MM-DD` string — a business date is
 * a calendar day, never an instant (docs/DATES.md). Internally the math runs
 * on LOCAL `Date`s built by `parseLocalISODate` and read back with
 * `formatLocalISODate`; there is no `toISOString()` slicing anywhere, and the
 * date-fns helpers used (`addDays`, `addMonths`, `startOfMonth`, `setDate`)
 * operate on calendar components, so a DST transition inside a weekly step
 * cannot move the day.
 *
 * No i18n lives here: `describeRecurrence(rule, t)` is web-side
 * (`apps/web/src/lib/recurrence.ts`) — no shared function takes a `t`.
 */
import { addDays, addMonths, getDaysInMonth, setDate, startOfMonth } from "date-fns";
import { formatLocalISODate, parseLocalISODate } from "../format/date.js";
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/** `recurring_document_templates.frequency`. */
export const RECURRING_FREQUENCIES = ["weekly", "monthly", "quarterly", "yearly"];
/**
 * Whole months per `interval_count` step for the month-based frequencies.
 * `weekly` is day-based and has no entry (the type says so).
 */
const MONTHS_PER_STEP = {
    monthly: 1,
    quarterly: 3,
    yearly: 12,
};
/**
 * The upper bound on `interval_count` per frequency — the twin of the table
 * CHECK `interval_count <= CASE frequency WHEN 'weekly' THEN 52 ELSE 12 END`.
 * The zod schema (`schemas/recurring.ts`) and the web form both read it from
 * here so the three cannot disagree.
 */
export function maxIntervalCountFor(frequency) {
    return frequency === "weekly" ? 52 : 12;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Parse a `YYYY-MM-DD` calendar day as a local `Date` or throw. Callers of
 * this module hand it validated schema output (`calendarDateSchema`), so an
 * unparseable value is a programming error, not user input.
 */
function toLocalDate(iso, label) {
    const parsed = parseLocalISODate(iso);
    if (!parsed || Number.isNaN(parsed.getTime())) {
        throw new RangeError(`${label} must be YYYY-MM-DD, got ${JSON.stringify(iso)}`);
    }
    return parsed;
}
function toISO(date) {
    // formatLocalISODate only returns null for an invalid Date; every Date this
    // module builds is derived from a validated one, so the fallback is unreachable.
    return formatLocalISODate(date) ?? "";
}
/** Day-of-month of a `YYYY-MM-DD` start date — the GENERATED `anchor_day`. */
export function anchorDayOf(startISO) {
    return toLocalDate(startISO, "start_date").getDate();
}
// ---------------------------------------------------------------------------
// The rule
// ---------------------------------------------------------------------------
/**
 * `recurring_next_occurrence_date(p_frequency, p_interval_count, p_anchor_day, p_from)`.
 *
 * The strict successor of `fromISO` under the rule — `fromISO` itself is
 * assumed to be an occurrence (or the start date). Weekly steps are day
 * arithmetic; month-based steps truncate to the month, add whole months, then
 * land on `min(anchor_day, days in that month)`.
 */
export function nextOccurrenceDate(rule, fromISO) {
    const from = toLocalDate(fromISO, "from");
    if (rule.frequency === "weekly") {
        return toISO(addDays(from, 7 * rule.interval_count));
    }
    const months = rule.interval_count * MONTHS_PER_STEP[rule.frequency];
    const m1 = addMonths(startOfMonth(from), months);
    return toISO(setDate(m1, Math.min(rule.anchor_day, getDaysInMonth(m1))));
}
/**
 * The step cap of the bounded search in `firstOccurrenceOnOrAfter` — the twin
 * of the SQL function's loop bound. 2000 weekly steps ≈ 38 years; 2000 monthly
 * steps ≈ 166 years. A template cannot legitimately need more (its `start_date`
 * is at or after org-today at create, and Resume/PATCH search forward from
 * `GREATEST(org-today, last_occurrence_date + 1)`).
 */
export const FIRST_OCCURRENCE_MAX_STEPS = 2000;
/**
 * `recurring_first_occurrence_on_or_after(p_frequency, p_interval_count, p_start, p_on_or_after)`.
 *
 * Walk the rule forward from `startISO` (anchor = day of `startISO`) and return
 * the first occurrence `>= onOrAfterISO`. When `onOrAfterISO <= startISO` that
 * is `startISO` itself. This is the Resume / PATCH recompute base: `next :=
 * first_on_or_after(new rule, start, GREATEST(org-today, last_occurrence_date + 1))`
 * — never from `start_date` alone, which would re-land on a generated date.
 *
 * Returns `null` if the walk exceeds `FIRST_OCCURRENCE_MAX_STEPS` (the DB twin
 * is bounded the same way) so a caller can never spin on a pathological span.
 */
export function firstOccurrenceOnOrAfter(cadence, startISO, onOrAfterISO) {
    // Validate both up front so a bad bound surfaces as a RangeError, not as a
    // silent lexicographic comparison against garbage.
    toLocalDate(onOrAfterISO, "on_or_after");
    const rule = { ...cadence, anchor_day: anchorDayOf(startISO) };
    let cursor = startISO;
    // Lexicographic order IS date order for YYYY-MM-DD.
    for (let step = 0; step <= FIRST_OCCURRENCE_MAX_STEPS; step += 1) {
        if (cursor >= onOrAfterISO)
            return cursor;
        cursor = nextOccurrenceDate(rule, cursor);
    }
    return null;
}
/**
 * The next `count` occurrence dates of a schedule, starting at
 * `options.from ?? schedule.start_date` and honouring `end_date` /
 * `max_occurrences − occurrences_generated`. Pure and synchronous — the web
 * "Next dates" preview calls it on every keystroke; there is no endpoint.
 *
 * Returns fewer than `count` (possibly none) when the bounds run out first.
 */
export function previewOccurrenceDates(schedule, count, options = {}) {
    const rule = {
        frequency: schedule.frequency,
        interval_count: schedule.interval_count,
        anchor_day: anchorDayOf(schedule.start_date),
    };
    const remainingByCap = options.max_occurrences == null
        ? Number.POSITIVE_INFINITY
        : Math.max(0, options.max_occurrences - (options.occurrences_generated ?? 0));
    const limit = Math.min(Math.max(0, Math.floor(count)), remainingByCap);
    const endDate = options.end_date ?? null;
    const dates = [];
    let cursor = options.from ?? schedule.start_date;
    toLocalDate(cursor, "from");
    while (dates.length < limit) {
        if (endDate != null && cursor > endDate)
            break;
        dates.push(cursor);
        cursor = nextOccurrenceDate(rule, cursor);
    }
    return dates;
}
//# sourceMappingURL=schedule.js.map